// Tiny markdown renderer for synthesised docs: headings, inline code/bold/em,
// fenced code, tables, blockquotes, and indentation-nested lists.

function esc(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function inline(s: string): string {
  return esc(s)
    .replace(/`([^`]+)`/g, "<code>$1</code>")
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/(^|[^*])\*([^*]+)\*/g, "$1<em>$2</em>");
}

export function renderMarkdown(md: string): string {
  const lines = md.split("\n");
  let html = "", i = 0;
  let inCode = false, inTable = false;
  const listStack: { tag: string; indent: number }[] = [];   // supports nested lists by indent
  const closeLists = () => { while (listStack.length) html += `</${listStack.pop()!.tag}>`; };
  const closeTable = () => { if (inTable) { html += "</tbody></table>"; inTable = false; } };
  const isListLine = (s: string) => /^\s*([-*]|\d+\.)\s+/.test(s);
  while (i < lines.length) {
    const line = lines[i]!;
    if (line.trim().startsWith("```")) {
      if (!inCode) { closeLists(); closeTable(); html += "<pre><code>"; inCode = true; }
      else { html += "</code></pre>"; inCode = false; }
      i++; continue;
    }
    if (inCode) { html += esc(line) + "\n"; i++; continue; }

    // table
    if (/^\s*\|.*\|\s*$/.test(line) && /^\s*\|[\s:|-]+\|\s*$/.test(lines[i + 1] || "")) {
      closeLists();
      const headers = line.split("|").slice(1, -1).map((s) => s.trim());
      html += `<table><thead><tr>${headers.map((h) => `<th>${inline(h)}</th>`).join("")}</tr></thead><tbody>`;
      inTable = true; i += 2;
      continue;
    }
    if (inTable) {
      if (/^\s*\|.*\|\s*$/.test(line)) {
        const cells = line.split("|").slice(1, -1).map((s) => s.trim());
        html += `<tr>${cells.map((c) => `<td>${inline(c)}</td>`).join("")}</tr>`;
        i++; continue;
      } else closeTable();
    }

    const h = line.match(/^(#{1,4})\s+(.*)$/);
    if (h) { closeLists(); html += `<h${h[1]!.length}>${inline(h[2]!)}</h${h[1]!.length}>`; i++; continue; }
    if (/^\s*>\s?/.test(line)) { closeLists(); html += `<blockquote>${inline(line.replace(/^\s*>\s?/, ""))}</blockquote>`; i++; continue; }

    // list item — indentation-aware nesting
    const listM = line.match(/^(\s*)([-*]|\d+\.)\s+(.*)$/);
    if (listM) {
      const ind = listM[1]!.length;
      const tag = /\d+\./.test(listM[2]!) ? "ol" : "ul";
      while (listStack.length && ind < listStack[listStack.length - 1]!.indent) html += `</${listStack.pop()!.tag}>`;
      const top = listStack[listStack.length - 1];
      if (!top || ind > top.indent) { html += `<${tag}>`; listStack.push({ tag, indent: ind }); }
      else if (top.tag !== tag) { html += `</${top.tag}>`; listStack.pop(); html += `<${tag}>`; listStack.push({ tag, indent: ind }); }
      html += `<li>${inline(listM[3]!)}</li>`;
      i++; continue;
    }
    // keep a list open across a single blank line between its items
    if (line.trim() === "") { if (!isListLine(lines[i + 1] || "")) closeLists(); i++; continue; }
    closeLists(); html += `<p>${inline(line)}</p>`; i++;
  }
  closeLists(); closeTable(); if (inCode) html += "</code></pre>";
  return html;
}
